/**
 * The timer's alerts across Shovel tabs (timer-alerts spec): none while any Shovel tab is in view (its pill asks), and
 * one alert for each moment however many tabs are open.
 */

const CHANNEL = 'shovel-timer-alerts';
// Long enough for a tab in view to answer; short enough not to make the chime late.
const ANSWER_MS = 250;

const alertedKey = (plannedTaskId: number) => `shovel.timerAlerted.${plannedTaskId}`;

let channel: BroadcastChannel | null = null;

/**
 * Whether the student is looking at this Shovel tab: in view and its window the one in use. A tab whose window sits
 * behind another app, not wholly covered, still reads as visible, as does Shovel side by side with Google Docs while
 * the student types there (Petr, 2026-10-31: chime but no notice).
 */
export const shovelInView = () => !document.hidden && document.hasFocus();

/** Runs once the student is looking at this tab: now if they are, otherwise as it comes forward. */
export const whenInView = (run: () => void) => {
  if (shovelInView()) return run();
  const check = () => {
    if (!shovelInView()) return;
    window.removeEventListener('focus', check);
    document.removeEventListener('visibilitychange', check);
    run();
  };
  window.addEventListener('focus', check);
  document.addEventListener('visibilitychange', check);
};

/** Answers other tabs asking whether one is in view. Returns the unsubscribe. */
export const answerTabsInView = () => {
  if (typeof BroadcastChannel === 'undefined') return () => undefined;
  channel = channel || new BroadcastChannel(CHANNEL);
  const own = channel;
  const answer = (event: MessageEvent) => {
    if (event.data?.type === 'inView?' && shovelInView()) own.postMessage({ type: 'inView', ask: event.data.ask });
  };
  own.addEventListener('message', answer);
  return () => own.removeEventListener('message', answer);
};

/** Whether another Shovel tab is in view, asked over the tabs' channel. */
export const otherTabInView = () =>
  new Promise<boolean>(resolve => {
    if (typeof BroadcastChannel === 'undefined') return resolve(false);
    const asking = new BroadcastChannel(CHANNEL);
    const ask = Math.random()
      .toString(36)
      .slice(2);
    const done = (inView: boolean) => {
      window.clearTimeout(timer);
      asking.close();
      resolve(inView);
    };
    asking.onmessage = event => {
      if (event.data?.type === 'inView' && event.data.ask === ask) done(true);
    };
    const timer = window.setTimeout(() => done(false), ANSWER_MS);
    asking.postMessage({ type: 'inView?', ask });
  });

/** The last check-in alerted for a session in this browser, -1 for none. */
export const readAlerted = (plannedTaskId: number) => {
  try {
    const value = window.localStorage.getItem(alertedKey(plannedTaskId));
    return value === null ? -1 : Number(value);
  } catch {
    return -1;
  }
};

export const writeAlerted = (plannedTaskId: number, checkIn: number) => {
  try {
    if (checkIn < 0) window.localStorage.removeItem(alertedKey(plannedTaskId));
    else window.localStorage.setItem(alertedKey(plannedTaskId), String(checkIn));
  } catch {
    // Private windows: each tab keeps its own count.
  }
};

/**
 * Claims a moment for this tab: true for the one tab that alerts. A Web Lock makes reading and marking it one step
 * across tabs; without locks, the mark alone.
 */
export const claimMoment = async (plannedTaskId: number, checkIn: number) => {
  const claim = () => {
    if (readAlerted(plannedTaskId) >= checkIn) return false;
    writeAlerted(plannedTaskId, checkIn);
    return true;
  };
  const locks = (navigator as any).locks;
  if (!locks?.request) return claim();
  try {
    return await locks.request(`shovel-timer-alert-${plannedTaskId}`, () => claim());
  } catch {
    return claim();
  }
};
